import { useEffect, useState } from 'preact/hooks';
import { CardGrid, Empty, ErrorBox, Loading, Segmented } from '../components/ui';
import { api, qs, type CardSummary, type CardType, type Page } from '../lib/api';
import { navigate } from '../lib/router';
import { errorText, prefs, setPref, toast, useAsync, useIsAdmin, type Prefs } from '../lib/store';

interface Tag {
  id: number;
  name: string;
  facet: string | null;
  count: number;
}

const TIME_OPTIONS: [string, string][] = [['', 'Any'], ['20', '≤ 20 min'], ['30', '≤ 30 min'], ['45', '≤ 45 min'], ['60', '≤ 1 h']];
const FACET_LABEL: Record<string, string> = { diet: 'Diet', cuisine: 'Cuisine', course: 'Course', season: 'Season', other: 'Tags' };

export function Browse({ query }: { query: URLSearchParams }) {
  const p = prefs.use();
  const admin = useIsAdmin();
  const q = query.get('q') ?? '';
  const type = query.get('type') ?? '';
  const tags = (query.get('tag') ?? '').split(',').filter(Boolean);
  const status = query.get('status') ?? '';
  const favourite = query.get('favourite') === '1';
  const needsReview = query.get('needs_review') === '1';
  const maxTime = query.get('max_total_min') ?? '';
  const minRating = query.get('min_rating') ?? '';
  const sort = query.get('sort') ?? (q ? 'relevance' : 'last_baked');
  const [railOpen, setRailOpen] = useState(false);
  const [items, setItems] = useState<CardSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(q);

  useEffect(() => setSearch(q), [q]);

  const meta = useAsync(async () => {
    const [t, ty] = await Promise.all([api.get<{ items: Tag[] }>('/tags'), api.get<{ items: CardType[] }>('/types')]);
    return { tags: t.items, types: ty.items };
  }, []);

  const params = { q, type, tag: tags, status, favourite: favourite ? 1 : undefined, needs_review: needsReview ? 1 : undefined, max_total_min: maxTime, min_rating: minRating, sort, limit: 24 };
  const list = useAsync(() => api.get<Page<CardSummary>>(`/cards${qs(params)}`), [query.toString()]);
  // Sorted by Recently baked, the results fall into two runs: baked, then still to bake. Each heading
  // carries its count across all pages (same filters, limit 1).
  const grouped = sort === 'last_baked';
  const counts = useAsync(
    async () =>
      grouped
        ? Promise.all(['yes', 'no'].map((baked) => api.get<Page<CardSummary>>(`/cards${qs({ ...params, baked, limit: 1 })}`).then((r) => r.total)))
        : null,
    [query.toString()],
  );

  useEffect(() => {
    if (list.data) {
      setItems(list.data.items);
      setCursor(list.data.next_cursor);
      setTotal(list.data.total);
    }
  }, [list.data]);

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(query);
    if (v) next.set(k, v);
    else next.delete(k);
    navigate(`/browse${next.toString() ? `?${next}` : ''}`, { replace: true });
  };

  const bakeGroups: { label: string; items: CardSummary[]; count: number | undefined }[] = [
    { label: 'Baked', items: items.filter((c) => c.last_used_at), count: counts.data?.[0] },
    { label: 'Still to bake', items: items.filter((c) => !c.last_used_at), count: counts.data?.[1] },
  ];

  // bulk approve: the flagged cards currently listed (owner only)
  const flagged = items.filter((c) => c.needs_review);
  const approveShown = async () => {
    if (!confirm(`Mark ${flagged.length === 1 ? 'this card' : `these ${flagged.length} cards`} as reviewed?`)) return;
    try {
      const { reviewed } = await api.post<{ reviewed: number }>('/cards/reviewed', { ids: flagged.map((c) => c.id) });
      toast(`Marked ${reviewed} ${reviewed === 1 ? 'card' : 'cards'} reviewed`);
      list.reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const toggleTag = (t: string) => setParam('tag', (tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]).join(','));

  const more = async () => {
    if (!cursor) return;
    const r = await api.get<Page<CardSummary>>(`/cards${qs({ ...params, cursor })}`);
    setItems((x) => [...x, ...r.items]);
    setCursor(r.next_cursor);
  };

  const byFacet = new Map<string, Tag[]>();
  for (const t of meta.data?.tags ?? []) {
    const f = t.facet ?? 'other';
    byFacet.set(f, [...(byFacet.get(f) ?? []), t]);
  }

  return (
    <div class="browse">
      <aside class={`rail ${railOpen ? '' : 'collapsed'}`} aria-label="Filters">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setParam('q', search.trim() || null);
          }}
        >
          <label for="q">Search</label>
          <input id="q" class="dr-input" type="search" placeholder="leek, crème, 30 min…" value={search} onInput={(e) => setSearch(e.currentTarget.value)} />
        </form>
        <button type="button" class="dr-btn bk-sm rail-toggle" aria-expanded={railOpen} onClick={() => setRailOpen(!railOpen)}>
          {railOpen ? 'Hide filters' : 'Filters'}
          {tags.length + (type ? 1 : 0) + (maxTime ? 1 : 0) + (favourite ? 1 : 0) + (needsReview ? 1 : 0) ? ` (${tags.length + (type ? 1 : 0) + (maxTime ? 1 : 0) + (favourite ? 1 : 0) + (needsReview ? 1 : 0)})` : ''}
        </button>
        <div class="rail-body stack">
          <div>
            <h3>Type</h3>
            <div class="chips">
              <button type="button" class="dr-tag" aria-pressed={!type} onClick={() => setParam('type', null)}>
                All
              </button>
              {meta.data?.types.map((t) => (
                <button type="button" class="dr-tag" aria-pressed={type === t.key} onClick={() => setParam('type', type === t.key ? null : t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3>Total time</h3>
            <select class="dr-input bk-input-sm" aria-label="Maximum total time" value={maxTime} onChange={(e) => setParam('max_total_min', e.currentTarget.value || null)}>
              {TIME_OPTIONS.map(([v, l]) => (
                <option value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div class="stack">
            <label class="checkbox">
              <input type="checkbox" checked={favourite} onChange={(e) => setParam('favourite', e.currentTarget.checked ? '1' : null)} /> Favourites only
            </label>
            {admin && (
              <label class="checkbox">
                <input type="checkbox" checked={needsReview} onChange={(e) => setParam('needs_review', e.currentTarget.checked ? '1' : null)} /> Needs review only
              </label>
            )}
            <div>
              <h3>Rating</h3>
              <select class="dr-input bk-input-sm" aria-label="Minimum rating" value={minRating} onChange={(e) => setParam('min_rating', e.currentTarget.value || null)}>
                <option value="">Any</option>
                {[5, 4, 3].map((n) => (
                  <option value={String(n)}>{'★'.repeat(n)}{n < 5 ? ' & up' : ''}</option>
                ))}
              </select>
            </div>
            {admin && (
            <div>
              <h3>Status</h3>
              <select class="dr-input bk-input-sm" aria-label="Status" value={status} onChange={(e) => setParam('status', e.currentTarget.value || null)}>
                <option value="">Active & drafts</option>
                <option value="active">Active</option>
                <option value="draft">Drafts</option>
                <option value="archived">Archived</option>
                <option value="all">Everything</option>
              </select>
            </div>
            )}
          </div>
          {[...byFacet.entries()].map(([facet, list]) => (
            <div>
              <h3>{FACET_LABEL[facet] ?? facet}</h3>
              <div class="chips">
                {list.slice(0, 24).map((t) => (
                  <button type="button" class="dr-tag" aria-pressed={tags.includes(t.name)} onClick={() => toggleTag(t.name)}>
                    {t.name} <span class="muted">{t.count}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
      <section aria-live="polite">
        <div class="row-between" style={{ marginBottom: 'var(--bk-space-4)' }}>
          <h1>{q ? `“${q}”` : 'Browse'}</h1>
          <div class="row">
            <select class="dr-input bk-input-sm" aria-label="Sort" value={sort} onChange={(e) => setParam('sort', e.currentTarget.value)}>
              {q && <option value="relevance">Best match</option>}
              <option value="last_baked">Recently baked</option>
              <option value="updated">Recently updated</option>
              <option value="created">Recently added</option>
              <option value="title">A–Z</option>
              <option value="rating">Rating</option>
              <option value="last_used">Not baked in a while</option>
            </select>
            <Segmented<Prefs['density']>
              label="Density"
              value={p.density}
              onChange={(v) => setPref('density', v)}
              options={[
                ['comfortable', '▦'],
                ['compact', '▤'],
                ['list', '☰'],
              ]}
            />
          </div>
        </div>
        <p class="muted small">{list.loading ? 'Searching…' : `${total} card${total === 1 ? '' : 's'}`}</p>
        {list.error ? <ErrorBox error={list.error} /> : null}
        {admin && flagged.length > 0 && (
          <div class="notice row-between review-bar">
            <span>
              {flagged.length} {flagged.length === 1 ? 'card here needs' : 'cards here need'} review
            </span>
            <button type="button" class="dr-btn bk-sm" onClick={() => void approveShown()}>
              Mark {flagged.length === 1 ? 'it' : `all ${flagged.length}`} reviewed
            </button>
          </div>
        )}
        {list.loading && !items.length ? (
          <Loading />
        ) : items.length ? (
          grouped ? (
            <>
              {bakeGroups.map((g) =>
                g.items.length ? (
                  <section class="bake-group" aria-label={g.label}>
                    <h2 class="bake-group-h">
                      {g.label}
                      {g.count !== undefined && <span class="muted"> · {g.count}</span>}
                    </h2>
                    <CardGrid items={g.items} />
                  </section>
                ) : null,
              )}
            </>
          ) : (
            <CardGrid items={items} />
          )
        ) : (
          !list.loading && <Empty>No cards match these filters.</Empty>
        )}
        {cursor && (
          <div class="row" style={{ justifyContent: 'center', marginTop: 'var(--bk-space-6)' }}>
            <button class="dr-btn" onClick={more}>
              Load more
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
